/**
 * Apps tab — pool membership: the tenancy policy (which projects' workers
 * this pool accepts) plus per-app membership via the explicit-opt-in model
 * (pool_inclusions + pool_refinements).
 */

import { PoolMembershipEditor } from '../profiles/PoolMembershipEditor';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import type { PoolConfigDraftApi } from './usePoolConfigDraft';

export interface AppsTabProps {
  poolId: string;
  /** Pool type ('by_request' | ...); undefined until the pool row loads. */
  poolType: string | undefined;
  draftApi: PoolConfigDraftApi;
  saving: boolean;
  authHeaders: () => Promise<Record<string, string>>;
  apiBaseUrl: string;
  onError: (msg: string) => void;
}

export function AppsTab({ poolId, poolType, draftApi, saving, authHeaders, apiBaseUrl, onError }: AppsTabProps) {
  const { draft, patch } = draftApi;
  return (
    <>
    {/* Tenancy — the membership policy the mapping below operates under.
        Edits the shared config draft; the page-level save bar appears when
        dirty. */}
    {draft && (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Tenancy</h2>
        </div>
        <div className={styles.formHelp}>
          Which projects' workers this pool accepts. Multi-tenant pools curate apps by
          explicit inclusion below.
        </div>
        <div className={styles.formRow}>
          <select
            id="pool-tenancy"
            aria-label="Tenancy"
            className={styles.formSelect}
            value={draft.tenancy}
            onChange={e => patch({ tenancy: e.target.value as 'single' | 'organization' | 'multi' })}
            disabled={saving}
          >
            <option value="single">Single — workers must match pool project</option>
            <option value="organization">Organization — workers from any project in the org</option>
            <option value="multi">Multi — workers from any project accepted</option>
          </select>
          {poolType === 'by_request' && draft.tenancy === 'single' && (
            <div className={styles.formWarn}>
              On-demand pools default to multi-tenancy. Single restricts workers to the pool's own project.
            </div>
          )}
        </div>
      </div>
    )}

    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>App Mappings (membership)</h2>
      </div>
      <PoolMembershipEditor
        poolId={poolId}
        authHeaders={authHeaders}
        apiBaseUrl={apiBaseUrl}
        onError={onError}
      />
    </div>
    </>
  );
}
